import React, { FC, Dispatch, SetStateAction } from "react";
import { Typography, IconButton, Tooltip } from "@mui/material";
import CanvasBox from "../CanvasBox";
import { Resizable } from "re-resizable";
import Portal from "../Portal";
import { styled } from "@mui/material/styles";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import usePreviewSelector from "./usePreviewSelector";

const StyledDivRowSpace = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  cursor: "move",
  backgroundColor: "#4b4b4b",
  padding: 5,
});

const StyledDivCol = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
});

const StyledDivFixed = styled("div")({
  position: "fixed",
  display: "flex",
  flexDirection: "column",
  cursor: "pointer",
  zIndex: 1500,
});

interface PreviewProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  showPortal: boolean;
  setShowPortal: Dispatch<SetStateAction<boolean>>;
}

const Preview: FC<PreviewProps> = ({
  open,
  setOpen,
  showPortal,
  setShowPortal,
}) => {
  const {
    resizeStop,
    handleClose,
    handlePortalClose,
    handlePortalOpen,
    onMouseMove,
    disableDragging,
    enableDragging,
    preview,
    size,
    setSize,
  } = usePreviewSelector(setOpen, setShowPortal);

  if (!open && showPortal) {
    return (
      <Portal handleClose={handlePortalClose}>
        <CanvasBox
          canvasWidth={size.width}
          canvasHeight={size.height}
          setSize={setSize}
        />
      </Portal>
    );
  }

  if (open && !showPortal) {
    return (
      <StyledDivFixed
        id={"node"}
        style={{
          top: preview.y + "px",
          left: preview.x + "px",
        }}
      >
        <Resizable
          size={size}
          onResizeStop={resizeStop}
          minHeight="350px"
          minWidth="350px"
          maxWidth="900px"
          maxHeight="900px"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "10px",
            background: "white",
            border: "1px solid #ddd",
            boxShadow: "0px 0px 10px rgba(0,0,0,0.5)",
            zIndex: 1000,
            overflow: "hidden",
          }}
        >
          <div style={{ width: "100%", height: "100%" }}>
            <StyledDivRowSpace
              onMouseDown={enableDragging}
              onMouseUp={disableDragging}
              onMouseMove={onMouseMove}
            >
              <Tooltip title="Open in New Window" placement="top">
                <IconButton onClick={handlePortalOpen}>
                  <OpenInNewIcon style={{ color: "white" }} />
                </IconButton>
              </Tooltip>

              <StyledDivCol>
                <Typography
                  variant="h6"
                  style={{ fontWeight: "bold", color: "white" }}
                >
                  2D PREVIEW
                </Typography>
                <Typography
                  variant="caption"
                  style={{ color: "white", fontSize: 10 }}
                >
                  AS VIEWED FROM EXTERIOR
                </Typography>
              </StyledDivCol>

              <IconButton onClick={handleClose}>
                <CloseIcon style={{ color: "white" }} />
              </IconButton>
            </StyledDivRowSpace>

            <StyledDivCol>
              <Typography variant="caption">
                All proportions and finishes are best approximations
              </Typography>
            </StyledDivCol>
            <CanvasBox
              canvasWidth={size.width}
              canvasHeight={size.height}
              setSize={setSize}
            />
          </div>
        </Resizable>
      </StyledDivFixed>
    );
  }

  return null;
};

export default Preview;
